/**
 * Tutorial Composable
 * Manages tutorial state, navigation, and API integration
 */
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from '@vue/composition-api';
import { getStepsForPage, hasTutorial } from './tutorialSteps';
import TutorialApiService from '../../services/TutorialApiService';

/**
 * Tutorial page order - defines the correct sequence
 * Note: These are the internal page names used in composables
 */
const TUTORIAL_PAGE_ORDER = ['exhibits', 'souls', 'relateds', 'hints'];

/**
 * API progress_detail uses the same keys as internal page names
 * (exhibits, souls, relateds, hints)
 */

/**
 * Page display names in Chinese
 */
const PAGE_DISPLAY_NAMES = {
  exhibits: '證物頁面',
  souls: '亡魂頁面',
  relateds: '關係人頁面',
  hints: '提示頁面'
};

/**
 * Tutorial composable for managing tutorial flow
 * @param {string} pageName - Page name (exhibits, hints, souls)
 * @param {Object} options - Configuration options
 * @param {string} options.memberLineId - Player's LINE ID
 * @param {string} options.groupId - Group ID
 * @returns {Object} Tutorial state and methods
 */
export function useTutorial(pageName, options = {}) {
  // State
  const isActive = ref(false);
  const isEnabled = ref(false);
  const currentStepIndex = ref(0);
  const targetRect = ref(null);
  const isTransitioning = ref(false);

  // Get steps for this page
  const steps = computed(() => getStepsForPage(pageName));
  const totalSteps = computed(() => steps.value.length);

  // Current step
  const currentStep = computed(() => {
    if (currentStepIndex.value >= 0 && currentStepIndex.value < steps.value.length) {
      return steps.value[currentStepIndex.value];
    }
    return null;
  });

  // Navigation state
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(() => currentStepIndex.value === totalSteps.value - 1);

  // Progress percentage
  const progress = computed(() => {
    if (totalSteps.value === 0) return 0;
    return Math.round(((currentStepIndex.value + 1) / totalSteps.value) * 100);
  });

  /**
   * Update target element position for spotlight
   */
  const updateTargetPosition = async () => {
    await nextTick();

    if (!currentStep.value || !currentStep.value.target) {
      targetRect.value = null;
      return;
    }

    const element = document.querySelector(currentStep.value.target);
    if (element) {
      let rect = element.getBoundingClientRect();
      const padding = currentStep.value.spotlightPadding || 8;

      // Get visual viewport offset (for keyboard on mobile)
      let offsetTop = 0;
      let offsetLeft = 0;
      if (window.visualViewport) {
        offsetTop = window.visualViewport.offsetTop;
        offsetLeft = window.visualViewport.offsetLeft;
      }

      // Check for additional target to combine bounds (for absolutely positioned children)
      if (currentStep.value.combineTarget) {
        const additionalElement = document.querySelector(currentStep.value.combineTarget);
        if (additionalElement) {
          const additionalRect = additionalElement.getBoundingClientRect();
          // Combine bounds
          const minTop = Math.min(rect.top, additionalRect.top);
          const minLeft = Math.min(rect.left, additionalRect.left);
          const maxRight = Math.max(rect.right, additionalRect.right);
          const maxBottom = Math.max(rect.bottom, additionalRect.bottom);
          rect = {
            top: minTop,
            left: minLeft,
            width: maxRight - minLeft,
            height: maxBottom - minTop,
            right: maxRight,
            bottom: maxBottom
          };
        }
      }

      // Apply visual viewport offset to correct for keyboard
      const adjustedTop = rect.top + offsetTop;
      const adjustedLeft = rect.left + offsetLeft;

      targetRect.value = {
        top: adjustedTop - padding,
        left: adjustedLeft - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        // Original rect for tooltip positioning
        originalTop: adjustedTop,
        originalLeft: adjustedLeft,
        originalWidth: rect.width,
        originalHeight: rect.height
      };
    } else {
      console.warn(`[useTutorial] Target element not found: ${currentStep.value.target}`);
      targetRect.value = null;
    }
  };

  /**
   * Start the tutorial
   */
  const startTutorial = async () => {
    if (!hasTutorial(pageName) || steps.value.length === 0) {
      console.warn(`[useTutorial] No tutorial steps for page: ${pageName}`);
      return;
    }

    currentStepIndex.value = 0;
    isActive.value = true;

    // Wait for DOM to be ready, then update position
    await nextTick();
    setTimeout(() => {
      updateTargetPosition();
    }, 100);
  };

  /**
   * Go to next step
   */
  const nextStep = async () => {
    if (isTransitioning.value) return;

    if (isLastStep.value) {
      await completeTutorial();
      return;
    }

    isTransitioning.value = true;
    currentStepIndex.value++;

    await nextTick();
    await updateTargetPosition();

    setTimeout(() => {
      isTransitioning.value = false;
    }, 300);
  };

  /**
   * Go to previous step
   */
  const prevStep = async () => {
    if (isTransitioning.value || isFirstStep.value) return;

    isTransitioning.value = true;
    currentStepIndex.value--;

    await nextTick();
    await updateTargetPosition();

    setTimeout(() => {
      isTransitioning.value = false;
    }, 300);
  };

  /**
   * Go to specific step
   * @param {number} index - Step index
   */
  const goToStep = async (index) => {
    if (isTransitioning.value) return;
    if (index < 0 || index >= totalSteps.value) return;

    isTransitioning.value = true;
    currentStepIndex.value = index;

    await nextTick();
    await updateTargetPosition();

    setTimeout(() => {
      isTransitioning.value = false;
    }, 300);
  };

  /**
   * Complete the tutorial (call API and hide)
   */
  const completeTutorial = async () => {
    isActive.value = false;

    // Call API to update progress
    if (options.memberLineId && options.groupId) {
      try {
        const result = await TutorialApiService.updateProgress({
          lineId: options.memberLineId,
          groupId: options.groupId,
          page: pageName
        });

        if (result.success) {
          console.log(`[useTutorial] Tutorial completed for page: ${pageName}`);
        }
      } catch (error) {
        console.error('[useTutorial] Failed to update tutorial progress:', error);
      }
    }
  };

  /**
   * Skip the tutorial (same as complete but user-initiated)
   */
  const skipTutorial = async () => {
    await completeTutorial();
  };

  /**
   * Get the correct tutorial page based on progress_detail from API
   * @param {Object} progressDetail - Object with completed pages (e.g., {"exhibits": "finished", "souls": "finished"})
   * @returns {string|null} The page user should be on, or null if current page is valid
   */
  const getCorrectTutorialPage = (progressDetail) => {
    // Find the first page that is NOT completed - that's where user should be
    for (const page of TUTORIAL_PAGE_ORDER) {
      // API uses same keys as internal page names (exhibits, souls, relateds, hints)
      if (!progressDetail || progressDetail[page] !== 'finished') {
        // This page is not completed, user should be here
        if (page === pageName) {
          return null; // Current page is correct
        } else {
          return page; // User should go to this page
        }
      }
    }

    // All pages completed
    return null;
  };

  // State for wrong page popup
  const showWrongPagePopup = ref(false);
  const correctTutorialPage = ref(null);

  /**
   * Get the component name for a tutorial page (used by changeJudge)
   * @param {string} page - Page name
   * @returns {string} Component name (lowercase to match game/index.vue)
   */
  const getPageComponentName = (page) => {
    const componentNames = {
      exhibits: 'exhibits',
      souls: 'souls',
      relateds: 'relateds',
      hints: 'hints'
    };
    return componentNames[page] || page;
  };

  /**
   * Check if user is on the wrong page and show popup
   * @param {string|null} correctPage - The page user should be on, or null if current page is valid
   * @returns {boolean} True if on wrong page
   */
  const checkWrongPage = (correctPage) => {
    // If correctPage is null, current page is valid
    if (correctPage === null) {
      return false;
    }
    // If correctPage doesn't match current page, show popup
    if (correctPage !== pageName) {
      correctTutorialPage.value = correctPage;
      showWrongPagePopup.value = true;
      return true;
    }
    return false;
  };

  /**
   * Redirect to the correct tutorial page
   * @param {Function} emit - Vue emit function from component
   */
  const redirectToCorrectPage = (emit) => {
    if (correctTutorialPage.value) {
      const componentName = getPageComponentName(correctTutorialPage.value);
      showWrongPagePopup.value = false;

      // Emit change-judge event to parent (game/index.vue)
      if (emit && typeof emit === 'function') {
        emit('change-judge', componentName);
      } else {
        // Fallback: dispatch custom event for parent to handle
        window.dispatchEvent(new CustomEvent('tutorial-redirect', {
          detail: { page: correctTutorialPage.value, componentName }
        }));
      }
    }
  };

  /**
   * Get display name for correct page
   */
  const correctPageDisplayName = computed(() => {
    return PAGE_DISPLAY_NAMES[correctTutorialPage.value] || '';
  });

  /**
   * Get component name for correct page (used for navigation)
   */
  const correctPageComponentName = computed(() => {
    if (!correctTutorialPage.value) return '';
    return getPageComponentName(correctTutorialPage.value);
  });

  /**
   * Initialize tutorial from API response
   * @param {Object} apiResponse - Response from page API
   */
  const initFromApiResponse = async (apiResponse) => {
    const tutorialState = TutorialApiService.parseTutorialState(apiResponse);

    isEnabled.value = tutorialState.isEnabled;

    if (tutorialState.shouldStart) {
      // Fetch tutorial progress from dedicated API
      const progressData = await TutorialApiService.getProgress({
        lineId: options.memberLineId,
        groupId: options.groupId
      });

      const progressDetail = progressData.progress_detail || {};

      // Check if user is on the correct page based on progress_detail
      const correctPage = getCorrectTutorialPage(progressDetail);

      if (checkWrongPage(correctPage)) {
        // User is on wrong page, don't start tutorial
        return;
      }

      // Delay start to ensure page is fully rendered
      setTimeout(() => {
        startTutorial();
      }, 500);
    }
  };

  // Handle window resize - update target position
  const handleResize = () => {
    // Skip if current step allows interaction (keyboard might be open)
    if (isActive.value && !(currentStep.value && currentStep.value.allowInteraction)) {
      updateTargetPosition();
    }
  };

  // Handle scroll - update target position
  const handleScroll = () => {
    // Always update position on scroll (unlike resize which may be keyboard)
    if (isActive.value) {
      updateTargetPosition();
    }
  };

  // Handle focus/blur events for input fields (keyboard show/hide on mobile)
  const handleFocusChange = () => {
    if (isActive.value) {
      // Multiple updates to catch keyboard animation
      setTimeout(() => updateTargetPosition(), 100);
      setTimeout(() => updateTargetPosition(), 300);
      setTimeout(() => updateTargetPosition(), 500);
    }
  };

  // Handle visualViewport resize (more reliable for keyboard on mobile)
  const handleViewportResize = () => {
    if (isActive.value) {
      updateTargetPosition();
    }
  };

  // Setup event listeners
  onMounted(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('focusin', handleFocusChange);
    window.addEventListener('focusout', handleFocusChange);

    // Visual viewport API for better keyboard detection
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }
  });

  // Cleanup event listeners
  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('focusin', handleFocusChange);
    window.removeEventListener('focusout', handleFocusChange);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
      window.visualViewport.removeEventListener('scroll', handleViewportResize);
    }
  });

  return {
    // State
    isActive,
    isEnabled,
    currentStepIndex,
    currentStep,
    steps,
    totalSteps,
    targetRect,
    isTransitioning,

    // Wrong page popup state
    showWrongPagePopup,
    correctTutorialPage,
    correctPageDisplayName,
    correctPageComponentName,

    // Computed
    isFirstStep,
    isLastStep,
    progress,

    // Methods
    startTutorial,
    nextStep,
    prevStep,
    goToStep,
    completeTutorial,
    skipTutorial,
    updateTargetPosition,
    initFromApiResponse,
    redirectToCorrectPage
  };
}

export default useTutorial;
